import { cx } from '@emotion/css';
import { Button as ButtonSDS, Icon, MessageContext } from '@snapchat/snap-design-system-marketing';
import type { FC } from 'react';
import { useContext } from 'react';

import { Config } from '../../config';
import { ElementLocation } from '../../context/ElementLocationContext/ElementLocationContext';
import { logger, logUserEvent } from '../../helpers/logging';
import { UserAction } from '../../types/events';
import { AddToCart } from '../AddToCart';
import { ShopifyContext } from '../ShopifyProvider';
import { CartItem } from './CartItem';
import { CartContext } from './CartProvider';
import { useCheckoutUrl, usePrice } from './hooks';
import { Price } from './Price';
import {
  activeWrapperCss,
  buttonWrapperCss,
  cartCloseButtonCss,
  cartCloseCss,
  cartInternalCss,
  cartOverlayActiveCss,
  cartOverlayCss,
  cartWrapperStyles,
  checkoutButtonCss,
  checkoutDisclaimerCss,
  featuredProductButtonCss,
  featuredProductImg,
  featuredProductPriceCss,
  featuredProductTitleCss,
  noCartItemsContainerCss,
  noCartItemsTitleCss,
  totalCss,
} from './styles';

export const Cart: FC = () => {
  const elementLocation = useContext(ElementLocation).elementLocation;
  const { formatMessage } = useContext(MessageContext);
  const { catalog, productOptionById, currencyCode } = useContext(ShopifyContext);
  const cart = useContext(CartContext);
  const featuredProductKey = Config.shopify?.options.featuredProduct ?? '';
  const featuredProduct = catalog[featuredProductKey];
  const checkoutUrl = useCheckoutUrl();

  const checkoutButtonCopy = formatMessage({
    id: 'cartCheckoutButton',
    defaultMessage: 'Checkout',
  });

  const disclaimer = formatMessage({
    id: 'cartCheckoutDisclaimer',
    defaultMessage: 'Shipping calculated at checkout',
  });

  const noCartItems = formatMessage({
    id: 'noCartItems',
    defaultMessage: 'Nothing added to cart yet',
  });

  const hasItems = cart.lines.length > 0;

  const totalFromCart = cart.lines.reduce((acc, line) => {
    const entry = productOptionById[line.optionId];

    if (entry) {
      return acc + entry.option.price * line.quantity;
    }

    return acc;
  }, 0);

  // Calculate deposit total: for products with selling plan deposit data, use the deposit amount
  // for the current currency instead of the full product price.
  const depositTotalFromCart = cart.lines.reduce((acc, line) => {
    const entry = productOptionById[line.optionId];

    if (entry) {
      const product = catalog[entry.productKey];
      const currencyEntry =
        product?.sellingPlan?.depositData && currencyCode
          ? product.sellingPlan.depositData[currencyCode]
          : undefined;
      const itemPrice = currencyEntry?.amount ?? entry.option.price;

      return acc + itemPrice * line.quantity;
    }

    return acc;
  }, 0);

  const hasDeposits = depositTotalFromCart !== totalFromCart;
  const total = usePrice(hasDeposits ? depositTotalFromCart : totalFromCart);

  const totalCopy = formatMessage(
    {
      id: hasDeposits ? 'cartDepositTotal' : 'cartTotal',
      defaultMessage: hasDeposits ? 'Due today {total}' : 'Total {total}',
    },
    { total }
  );

  // Log the checkout interaction and take the user to the Shopify site.
  const onCheckoutClick = (): void => {
    logUserEvent({
      eventAction: UserAction.Click,
      eventCategory: 'CheckoutButton',
      eventLabel: null,
      context: { targetUrl: checkoutUrl.href, elementText: checkoutButtonCopy, elementLocation },
    });
    void logger.flush();
  };

  return (
    <div
      className={cx(cartWrapperStyles(hasItems), {
        [activeWrapperCss]: cart?.cartOpen,
      })}
    >
      <div
        onClick={() => cart.setCartOpen(false)}
        className={cx(cartOverlayCss, {
          [cartOverlayActiveCss]: cart?.cartOpen,
        })}
      />
      <div className={buttonWrapperCss}>
        <button className={cartCloseCss} onClick={() => cart.setCartOpen(false)}>
          <Icon name="cross" className={cartCloseButtonCss} />
        </button>
      </div>
      <div className={cartInternalCss} />
      {cart.lines.map(line => {
        const entry = productOptionById[line.optionId];

        if (!entry) return null;

        const product = catalog[entry.productKey];

        if (!product) return null;

        // TODO Phase 2: title and image come from Shopify only. If Contentful overrides
        // are needed (localized titles, branded images), add displayTitle/displayImage
        // to CatalogProduct and prefer them here.
        return (
          <CartItem
            key={line.optionId}
            productKey={entry.productKey}
            title={product.title}
            image={product.featuredImage ?? ''}
            price={entry.option.price}
            sku={entry.option.sku}
            optionId={entry.option.id}
            optionTitle={entry.option.title}
            maxQuantity={product.maxQuantity}
            atpMessage={product.atpMessage}
            sellingPlanDepositData={product.sellingPlan?.depositData}
          />
        );
      })}
      {!hasItems && (
        <div className={noCartItemsContainerCss}>
          <div className={noCartItemsTitleCss}>{noCartItems}</div>
          {featuredProduct && (
            <>
              <div className={featuredProductImg(featuredProduct.featuredImage ?? '')} />
              <div className={featuredProductTitleCss}>{featuredProduct.title}</div>
              <Price
                className={featuredProductPriceCss}
                productPrice={featuredProduct.options[0]?.price ?? 0}
              />
              <AddToCart className={featuredProductButtonCss} productKey={featuredProductKey} />
            </>
          )}
        </div>
      )}
      {hasItems && (
        <>
          <div className={totalCss}>{totalCopy}</div>
          <div className={checkoutButtonCss}>
            <ButtonSDS link={checkoutUrl.href} type="Primary" onClick={onCheckoutClick}>
              {checkoutButtonCopy}
            </ButtonSDS>
          </div>
          <div className={checkoutDisclaimerCss}>{disclaimer}</div>
        </>
      )}
    </div>
  );
};
